/**
 * The animation that plays beside a stage's live numbers.
 *
 * Steps 1–3 are two to three minutes of a panel that had nothing in it, because
 * the type cards it was built for cannot exist until step 4 has run. This fills
 * that space with what the stage is actually doing — and never on its own: every
 * placement pairs the loop with a line of real, measured information, because a
 * decorative loop over a frozen counter is what made this screen read as hung in
 * the first place.
 *
 * One animation holds for a few seconds, fades out, and the next fades in; when a
 * step's set is exhausted it returns to the first and keeps going for as long as
 * the step lasts. A step that ends early simply never reaches the later frames.
 */
import { useEffect, useRef, useState } from 'react';

import pageFeed from './assets/stage-animations/01-page-feed.svg';
import deskew from './assets/stage-animations/02-deskew.svg';
import blankDetect from './assets/stage-animations/03-blank-detect.svg';
import despeckle from './assets/stage-animations/22-despeckle.svg';
import rotateUpright from './assets/stage-animations/23-rotate-upright.svg';
import cropEdges from './assets/stage-animations/24-crop-edges.svg';

import scanBeam from './assets/stage-animations/08-scan-beam.svg';
import barcodeDecode from './assets/stage-animations/09-barcode-decode.svg';
import glyphResolve from './assets/stage-animations/10-glyph-resolve.svg';
import wordHighlight from './assets/stage-animations/11-word-highlight.svg';
import magnifier from './assets/stage-animations/12-magnifier.svg';
import confidenceRise from './assets/stage-animations/13-confidence-rise.svg';
import ocrGrid from './assets/stage-animations/31-ocr-grid.svg';
import lineTracker from './assets/stage-animations/32-line-tracker.svg';

import boundaryCut from './assets/stage-animations/15-boundary-cut.svg';
import pagesCluster from './assets/stage-animations/16-pages-cluster.svg';
import sortingLanes from './assets/stage-animations/19-sorting-lanes.svg';
import headerChange from './assets/stage-animations/41-header-change.svg';
import pageNumberReset from './assets/stage-animations/42-page-number-reset.svg';
import lengthHistogram from './assets/stage-animations/46-length-histogram.svg';

import patternRepeat from './assets/stage-animations/60-pattern-repeat.svg';
import lockIn from './assets/stage-animations/57-lock-in.svg';

/** What the engine has actually reported, for frames whose claim depends on it. */
export type StageSignals = { barcodeCount: number };

/**
 * A frame is a picture, the sentence it is allowed to make, and — where that
 * sentence depends on something having happened — the evidence it needs.
 *
 * A frame without `requires` may only carry a caption that is true of every run
 * of that stage. "Reading each page, line by line" qualifies; "Decoding barcodes"
 * does not, because a batch can contain none, and a barcode animation over a
 * batch with zero barcodes is a picture of work that is not being done.
 */
export type StageFrame = {
  src: string;
  caption: string;
  requires?: (signals: StageSignals) => boolean;
};

/**
 * Captions describe the WORK, never the result.
 *
 * Nothing here may claim a document type, a field, or a count — those belong to
 * the live readouts beside this, which take them from the engine. A caption that
 * invented a finding would be the same defect as a stage titled "Discovering
 * types and fields" that never shows one.
 */
export const STAGE_FRAMES: Record<string, StageFrame[]> = {
  preparing: [
    { src: pageFeed, caption: 'Taking pages in, in the order they were fed' },
    // CHECKING, NOT HAVING FOUND.
    //
    // These read "straightening pages that came through at an angle" and the
    // rest in the same voice, each asserting that something had happened. The
    // engine runs every one of these checks on every page — PageDeskew,
    // PageInkGrid, the orientation and border passes — but it does not report
    // how many pages needed one, so a batch of clean upright scans was told its
    // pages were being straightened and its grit cleared. The check is the part
    // that is always true, so the check is what they say.
    { src: deskew, caption: 'Checking each page for skew, straightening what needs it' },
    { src: blankDetect, caption: 'Checking for blank sides - nothing is discarded' },
    { src: despeckle, caption: 'Checking for scanner grit, so it is not read as text' },
    { src: rotateUpright, caption: 'Checking orientation, turning what came in sideways' },
    { src: cropEdges, caption: 'Checking the page edges against the scanner border' },
  ],
  recognizing: [
    { src: scanBeam, caption: 'Reading each page, line by line' },
    // Shown only once a barcode has actually been decoded. This is the one claim
    // on this screen with a hard number behind it, and a batch can easily carry
    // none — a scanning beam over a barcode that does not exist is the same lie
    // as a discovery panel with nothing in it.
    {
      src: barcodeDecode,
      caption: 'Decoding barcodes into their values',
      requires: (s) => s.barcodeCount > 0,
    },
    { src: glyphResolve, caption: 'Resolving characters where the scan is soft' },
    { src: wordHighlight, caption: 'Following the page in its reading order' },
    { src: magnifier, caption: 'Looking closer where the evidence is thin' },
    { src: confidenceRise, caption: 'Weighing how certain each reading is' },
    { src: ocrGrid, caption: 'Turning marks on paper into characters' },
    { src: lineTracker, caption: 'Holding the line order the page was written in' },
  ],
  organizing: [
    { src: boundaryCut, caption: 'Finding where one document ends and the next begins' },
    { src: pagesCluster, caption: 'Gathering pages that belong to the same document' },
    { src: sortingLanes, caption: 'Routing each document by what it looks like' },
    { src: headerChange, caption: 'Watching for a new header - it usually means a new document' },
    { src: pageNumberReset, caption: 'Watching page numbering restart' },
    { src: lengthHistogram, caption: 'Checking the documents are coming out a sensible length' },
  ],
  understanding: [{ src: patternRepeat, caption: 'Matching what repeats across these documents' }],
  finalizing: [{ src: lockIn, caption: "Writing what it learned into this job's setup" }],
};

/** How long one frame holds before the crossfade begins. */
const HOLD_MS = 2800;
/** Must match the CSS transition on .stage-animation img. */
const FADE_MS = 420;

export function StageAnimation({
  stage,
  signals,
  className,
}: {
  stage: string | undefined;
  signals: StageSignals;
  className?: string;
}) {
  // A frame whose evidence has not arrived is not in the rotation at all, rather
  // than shown and skipped: on a batch with no barcodes the barcode frame must
  // never appear, not appear briefly. If evidence arrives mid-stage the frame
  // joins the loop from then on, which is honest in both directions.
  const frames = ((stage && STAGE_FRAMES[stage]) || []).filter(
    (frame) => !frame.requires || frame.requires(signals),
  );
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timers = useRef<number[]>([]);

  // Back to the first frame whenever the stage changes, so a step never opens
  // midway through another step's sequence.
  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [stage]);

  useEffect(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
    // A restart can cancel a crossfade half done (the frame count changed mid-fade): the
    // frame shows again instead of staying faded out until the next swap.
    setVisible(true);
    // A single frame is not a slideshow. It holds, which is also why a one-frame
    // step costs no timers at all.
    if (frames.length < 2) return;
    const out = window.setTimeout(() => {
      setVisible(false);
      const swap = window.setTimeout(() => {
        setIndex((current) => (current + 1) % frames.length);
        setVisible(true);
      }, FADE_MS);
      timers.current.push(swap);
    }, HOLD_MS);
    timers.current.push(out);
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    };
  }, [index, frames.length, stage]);

  const frame = frames[index];
  if (!frame) return null;
  return (
    <div className={`stage-animation ${className ?? ''}`.trim()}>
      {/* alt carries the caption because the caption IS the description; a second
          wording would be one more thing that can drift out of step. */}
      <img src={frame.src} alt={frame.caption} style={{ opacity: visible ? 1 : 0 }} />
      <small style={{ opacity: visible ? 1 : 0 }}>{frame.caption}</small>
    </div>
  );
}
